import { type TypedDocumentNode, gql } from '@apollo/client';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  FiatCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Box } from '@sorare/core/src/atoms/icons/Box';
import { Card } from '@sorare/core/src/atoms/icons/Card';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { StatDisplay } from '@sorare/core/src/atoms/ui/StatDisplay';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import { unitMapping } from '@sorare/core/src/lib/style';

import { useRewardsVerification } from 'components/so5/LastFixtureStory/RewardsRecap/useRewardsVerification';

import type {
  NeonRewards_anyRewardConfigInterface,
  NeonRewards_so5RewardOrConfig,
} from './__generated__/index.graphql';

const Wrapper = styled(Horizontal)`
  justify-content: space-evenly;
  row-gap: var(--unit);
  flex-wrap: wrap;
`;

type Props = {
  size?: 56 | 32 | 24;
  iconSize?: keyof typeof unitMapping;
  className?: string;
  displayLabels?: boolean;
  displayCommonEssence?: boolean;
  so5RewardsOrConfigs: NeonRewards_so5RewardOrConfig[];
};

export const NeonRewards = ({
  className,
  size = 56,
  iconSize = 4,
  so5RewardsOrConfigs,
  displayLabels = true,
  displayCommonEssence = true,
}: Props) => {
  const { formatNumber } = useIntlContext();
  const { exchangeRate } = useConfigContext();
  const so5Rewards = useMemo(
    () =>
      so5RewardsOrConfigs
        .map(so5RewardOrConfig =>
          isType(so5RewardOrConfig, 'So5Reward') ? so5RewardOrConfig : null
        )
        .filter(Boolean),
    [so5RewardsOrConfigs]
  );
  const { verificationNeeded } = useRewardsVerification(so5Rewards);

  const rewardConfigs = so5RewardsOrConfigs.flatMap(so5RewardOrConfig =>
    isType(so5RewardOrConfig, 'So5RewardConfig')
      ? so5RewardOrConfig.rewardConfigs
      : so5RewardOrConfig.rewards.map(reward => reward.config)
  );

  // CARD CONFIG
  const cardRewardConfigs = rewardConfigs.filter(isTypeFn('CardRewardConfig'));

  // SHARD CONFIG
  const shardRewardConfigs = rewardConfigs
    .filter(isTypeFn('CardShardRewardConfig'))
    .filter(({ rarity }) => rarity !== Rarity.common || displayCommonEssence);
  const shardCountsPerRarity = shardRewardConfigs.reduce(
    (acc, rewardConfig) => {
      const { rarity, quantity } = rewardConfig;

      return {
        ...acc,
        [rarity]: (acc[rarity] || 0) + quantity,
      };
    },
    {} as Record<Rarity, number>
  );

  const shardTotalCount = Object.entries(shardCountsPerRarity).reduce(
    (acc, [, count]) => acc + count,
    0
  );

  // CASH CONFIG
  const cashRewardConfigs = rewardConfigs.filter(
    isTypeFn('MonetaryRewardConfig')
  );
  const totalCash = cashRewardConfigs.reduce(
    (acc, rewardConfig) =>
      acc +
      new MonetaryAmount(rewardConfig.monetaryAmount).inCurrencies(
        exchangeRate.ethRates
      ).usd /
        100,
    0
  );

  // BOX CONFIG
  const boxRewardConfigs = rewardConfigs.filter(
    isTypeFn('ProbabilisticBundleRewardConfig')
  );

  const formattedTotalCash = formatNumber(totalCash, {
    style: 'currency',
    currency: FiatCurrency.USD,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  });

  const orderedRewards = [
    cashRewardConfigs.length > 0 && (
      <StatDisplay
        key="cash"
        size={size}
        label={
          <Horizontal>
            {verificationNeeded?.kyc && (
              <FontAwesomeIcon
                icon={faExclamationCircle}
                color="var(--c-yellow-400)"
              />
            )}
            {displayLabels && (
              <BodyS
                color={
                  verificationNeeded?.kyc ? 'var(--c-yellow-400)' : undefined
                }
              >
                <FormattedMessage {...glossary.cash} />
              </BodyS>
            )}
          </Horizontal>
        }
      >
        {formattedTotalCash}
      </StatDisplay>
    ),
    cardRewardConfigs.length > 0 && (
      <StatDisplay
        key="card"
        size={size}
        icon={<Card size={iconSize} gradient="blue" />}
        label={
          <Horizontal>
            {verificationNeeded?.phoneNumber && (
              <FontAwesomeIcon
                icon={faExclamationCircle}
                color="var(--c-yellow-400)"
              />
            )}
            {displayLabels && (
              <BodyS
                color={
                  verificationNeeded?.phoneNumber
                    ? 'var(--c-yellow-400)'
                    : undefined
                }
              >
                <FormattedMessage {...glossary.cards} />
              </BodyS>
            )}
          </Horizontal>
        }
      >
        {cardRewardConfigs.length}
      </StatDisplay>
    ),
    shardTotalCount > 0 && (
      <StatDisplay
        key="shard"
        size={size}
        icon={<Shards size={iconSize} gradient="purple" />}
        rotateIcon={false}
        label={
          <Horizontal>
            {verificationNeeded?.phoneNumber && (
              <FontAwesomeIcon
                icon={faExclamationCircle}
                color="var(--c-yellow-400)"
              />
            )}
            {displayLabels && (
              <BodyS
                color={
                  verificationNeeded?.phoneNumber
                    ? 'var(--c-yellow-400)'
                    : undefined
                }
              >
                <FormattedMessage {...glossary.essence} />
              </BodyS>
            )}
          </Horizontal>
        }
      >
        {shardTotalCount}
      </StatDisplay>
    ),
    boxRewardConfigs.length > 0 && (
      <StatDisplay
        key="box"
        size={size}
        icon={<Box size={iconSize} gradient="red" />}
        label={
          <Horizontal>
            {verificationNeeded?.phoneNumber && (
              <FontAwesomeIcon
                icon={faExclamationCircle}
                color="var(--c-yellow-400)"
              />
            )}
            {displayLabels && (
              <BodyS
                color={
                  verificationNeeded?.phoneNumber
                    ? 'var(--c-yellow-400)'
                    : undefined
                }
              >
                <FormattedMessage {...fantasy.boxes} />
              </BodyS>
            )}
          </Horizontal>
        }
      >
        {boxRewardConfigs.length}
      </StatDisplay>
    ),
  ].filter(Boolean);

  return <Wrapper className={className}>{orderedRewards.slice(0, 3)}</Wrapper>;
};

const anyRewardConfigInterfaceFragment = gql`
  fragment NeonRewards_anyRewardConfigInterface on AnyRewardConfigInterface {
    id
    ... on MonetaryRewardConfig {
      id
      monetaryAmount: amount {
        referenceCurrency
        eur
        gbp
        usd
        wei
      }
    }
    ... on ProbabilisticBundleRewardConfig {
      id
      probabilisticBundleConfig {
        id
        rarity
        stars
      }
    }
    ... on CardShardRewardConfig {
      id
      quantity
      rarity
    }
    ... on InGameCurrencyRewardConfig {
      id
      amount
      currency
    }
  }
` as TypedDocumentNode<NeonRewards_anyRewardConfigInterface>;

NeonRewards.fragments = {
  so5RewardOrConfig: gql`
    fragment NeonRewards_so5RewardOrConfig on So5RewardOrConfig {
      ... on So5RewardConfig {
        score
        ranks
        rankPct
        rewardConfigs {
          id
          ...NeonRewards_anyRewardConfigInterface
        }
      }
      ... on So5Reward {
        slug
        rewards {
          id
          config {
            id
            ...NeonRewards_anyRewardConfigInterface
          }
        }
        ...useRewardsVerification_so5Reward
      }
    }
    ${anyRewardConfigInterfaceFragment}
    ${useRewardsVerification.fragments.so5Reward}
  ` as TypedDocumentNode<NeonRewards_so5RewardOrConfig>,
};
